import { UserRole } from '../users/constant';

export const RESOURCE_CENTER_FILE_TYPE = '14';

export const DOCUMENT_THUMBNAIL_MAX_FILE_SIZE = 5 * 1024 * 1024;
export const DOCUMENT_THUMBNAIL_MAX_FILE_SIZE_ERROR = 'Thumbnail should be max 5MB';
export const DOCUMENT_THUMBNAIL_ACCEPTED_MIME_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png']
};

export const DOC_VISIBILITY = {
  ORG_WIDE_RESIDENTS: 1,
  ORG_WIDE_ADMINS: 2,
  ADMIN_ONLY: 3,
  RESIDENTS: 4
};

/** Multi-building managers, org managers, and super-admins can see/edit scope. */
export const canChooseDocOrgScope = (roleId, buildingCount = 0) =>
  roleId === UserRole.SUPER_ADMIN ||
  roleId === UserRole.ADMIN ||
  (roleId === UserRole.MANAGER && buildingCount > 1);

/** Org managers and super-admins can set scope to all buildings in the org. */
export const canChooseDocOrgWideScope = (roleId) =>
  roleId === UserRole.SUPER_ADMIN || roleId === UserRole.ADMIN;

export const DOC_SCOPE = {
  BUILDING: 'building',
  ORG: 'org'
};

export const DOC_ACCESS = {
  RESIDENTS_AND_ADMIN: 'residents_and_admin',
  ADMIN_ONLY: 'admin_only'
};

export const DOC_SCOPE_OPTIONS = [
  {
    value: DOC_SCOPE.BUILDING,
    label: 'This building',
    description: 'Only available for this building.'
  },
  {
    value: DOC_SCOPE.ORG,
    label: 'All buildings in org',
    description: 'Shared across every building in the organization.'
  }
];

export const getDocScopeOptions = (showScopeOptions, canChooseOrgWide = false) => {
  if (!showScopeOptions) {
    return [DOC_SCOPE_OPTIONS.find((option) => option.value === DOC_SCOPE.BUILDING)];
  }

  const options = [
    {
      value: DOC_SCOPE.BUILDING,
      label: 'A specific building',
      description: 'Only available for the selected building.'
    }
  ];

  if (canChooseOrgWide) {
    options.push(DOC_SCOPE_OPTIONS.find((option) => option.value === DOC_SCOPE.ORG));
  }

  return options;
};

export const DOC_ACCESS_OPTIONS = [
  {
    value: DOC_ACCESS.RESIDENTS_AND_ADMIN,
    label: 'Residents + admin',
    description: 'Visible in the Admin Portal, PM and Resident app under Documents.'
  },
  {
    value: DOC_ACCESS.ADMIN_ONLY,
    label: 'Admin only',
    description: 'Only visible to Admin Portal and PM app users'
  }
];

export const DOC_VISIBILITY_OPTIONS = [
  { value: DOC_VISIBILITY.RESIDENTS, label: 'Residents' },
  { value: DOC_VISIBILITY.ADMIN_ONLY, label: 'Admin only' },
  { value: DOC_VISIBILITY.ORG_WIDE_ADMINS, label: 'Org Wide Admins' },
  { value: DOC_VISIBILITY.ORG_WIDE_RESIDENTS, label: 'Org Wide Residents' }
];

export const DOC_VISIBILITY_MAP = DOC_VISIBILITY_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

/** Soft badge variants matching the blue "open" style, one color per visibility. */
export const DOC_VISIBILITY_BADGE_VARIANT = {
  [DOC_VISIBILITY.RESIDENTS]: 'open', // blue
  [DOC_VISIBILITY.ADMIN_ONLY]: 'inProgress', // orange
  [DOC_VISIBILITY.ORG_WIDE_ADMINS]: 'archived', // purple
  [DOC_VISIBILITY.ORG_WIDE_RESIDENTS]: 'complete' // teal
};

export const isGlobalDocVisibility = (visibility) =>
  visibility === DOC_VISIBILITY.ORG_WIDE_RESIDENTS ||
  visibility === DOC_VISIBILITY.ORG_WIDE_ADMINS;

/**
 * Org managers and super-admins can edit/delete any document.
 * Building / multi-building managers can only manage building-scoped docs
 * for buildings they have access to. Org-wide docs are view/download only.
 */
export const canManageDocument = (document, roleId, accessibleBuildingIds = []) => {
  if (!document) {
    return false;
  }

  if (roleId === UserRole.SUPER_ADMIN || roleId === UserRole.ADMIN) {
    return true;
  }

  if (roleId !== UserRole.MANAGER) {
    return false;
  }

  if (isGlobalDocVisibility(document.visibility)) {
    return false;
  }

  const docBuildingId = document.building ?? document.building_id;
  if (!docBuildingId || Number(docBuildingId) <= 0) {
    return false;
  }

  return accessibleBuildingIds.some((id) => Number(id) === Number(docBuildingId));
};

/** Org managers and super-admins can set folder shared=true. */
export const canSetFolderShared = (roleId) =>
  roleId === UserRole.SUPER_ADMIN || roleId === UserRole.ADMIN;

/**
 * Shared folders hold any file in the org. A building-local folder only holds files
 * scoped to that same building, so org-wide files never fit in one. The API enforces
 * this on move and clears the folder when a file's scope stops matching.
 */
export const canFolderHoldDoc = (folder, { isOrgWide, buildingId } = {}) => {
  if (!folder) {
    return false;
  }

  if (folder.shared) {
    return true;
  }

  if (isOrgWide) {
    return false;
  }

  const folderBuildingId = folder.building_id ?? folder.buildingId;
  return (
    Number(folderBuildingId) > 0 && Number(folderBuildingId) === Number(buildingId)
  );
};

/** API error text for a file that does not fit the destination folder's scope. */
export const FOLDER_SCOPE_MISMATCH_MESSAGE = 'document does not match folder scope';

export const isFolderScopeMismatchError = (err) => {
  const data = err?.data;
  const message = typeof data === 'string' ? data : data?.message || '';
  return String(message).toLowerCase().includes(FOLDER_SCOPE_MISMATCH_MESSAGE);
};

/**
 * Org managers / super-admins can manage any folder.
 * Building managers can manage folders whose building they can access.
 */
export const canManageFolder = (folder, roleId, accessibleBuildingIds = []) => {
  if (!folder) {
    return false;
  }

  if (roleId === UserRole.SUPER_ADMIN || roleId === UserRole.ADMIN) {
    return true;
  }

  if (roleId !== UserRole.MANAGER) {
    return false;
  }

  const folderBuildingId = folder.building_id ?? folder.buildingId;
  if (!folderBuildingId || Number(folderBuildingId) <= 0) {
    return false;
  }

  return accessibleBuildingIds.some((id) => Number(id) === Number(folderBuildingId));
};

/** Sentinel for "root" (no folder) in folder select UIs. */
export const DOCUMENT_ROOT_FOLDER_VALUE = 'root';

export const normalizeFolderSelectValue = (value) => {
  if (value === DOCUMENT_ROOT_FOLDER_VALUE || value == null || Number(value) <= 0) {
    return DOCUMENT_ROOT_FOLDER_VALUE;
  }
  return Number(value);
};

export const folderSelectValueToId = (value) => {
  if (value === DOCUMENT_ROOT_FOLDER_VALUE || value == null || Number(value) <= 0) {
    return null;
  }
  return Number(value);
};

export const isFolderRow = (item) => item?._rowType === 'folder' || item?.isFolder === true;

export const toFolderTableRow = (folder) => ({
  ...folder,
  id: folder.id,
  name: folder.name,
  building_id: folder.building_id,
  building_name: folder.building_name,
  visibility: folder.shared ? 'shared' : null,
  create_date: folder.create_date,
  file_name: '',
  file_size: folder.doc_count != null ? String(folder.doc_count) : '-',
  _rowType: 'folder',
  isFolder: true
});

export const resolveDocVisibility = (scope, access) => {
  if (scope === DOC_SCOPE.ORG) {
    return access === DOC_ACCESS.ADMIN_ONLY
      ? DOC_VISIBILITY.ORG_WIDE_ADMINS
      : DOC_VISIBILITY.ORG_WIDE_RESIDENTS;
  }

  return access === DOC_ACCESS.ADMIN_ONLY
    ? DOC_VISIBILITY.ADMIN_ONLY
    : DOC_VISIBILITY.RESIDENTS;
};

export const parseDocVisibility = (visibility) => {
  switch (visibility) {
    case DOC_VISIBILITY.ORG_WIDE_ADMINS:
      return { scope: DOC_SCOPE.ORG, access: DOC_ACCESS.ADMIN_ONLY };
    case DOC_VISIBILITY.ORG_WIDE_RESIDENTS:
      return { scope: DOC_SCOPE.ORG, access: DOC_ACCESS.RESIDENTS_AND_ADMIN };
    case DOC_VISIBILITY.ADMIN_ONLY:
      return { scope: DOC_SCOPE.BUILDING, access: DOC_ACCESS.ADMIN_ONLY };
    case DOC_VISIBILITY.RESIDENTS:
    default:
      return { scope: DOC_SCOPE.BUILDING, access: DOC_ACCESS.RESIDENTS_AND_ADMIN };
  }
};

export const DOCUMENT_FILE_TYPE_OPTIONS = [
  { value: 'pdf', label: 'PDF' },
  { value: 'doc', label: 'DOC' },
  { value: 'docx', label: 'DOCX' },
  { value: 'xls', label: 'XLS' },
  { value: 'xlsx', label: 'XLSX' },
  { value: 'ppt', label: 'PPT' },
  { value: 'pptx', label: 'PPTX' },
  { value: 'jpg', label: 'JPG' },
  { value: 'jpeg', label: 'JPEG' },
  { value: 'png', label: 'PNG' },
  { value: 'mp4', label: 'VID' }
];

export const DOCUMENT_ACCEPTED_MIME_TYPES = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'video/mp4': ['.mp4']
};

export const DOCUMENT_UPLOAD_TYPE_HINT = DOCUMENT_FILE_TYPE_OPTIONS.map(
  (option) => option.label
).join(', ');

export const DOCUMENT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
export const DOCUMENT_MAX_VIDEO_SIZE_BYTES = 500 * 1024 * 1024;

export const DOCUMENT_MAX_FILE_SIZE_ERROR = 'File should be max 10MB';
export const DOCUMENT_MAX_VIDEO_SIZE_ERROR = 'Video should be max 500MB';

export const DOCUMENT_VIDEO_CONTENT_TYPE = 'video/mp4';

export const isDocumentVideoFile = (file) => {
  if (!file?.name) {
    return false;
  }
  const name = file.name.toLowerCase();
  return name.endsWith('.mp4') || file.type === DOCUMENT_VIDEO_CONTENT_TYPE;
};

export const getDocumentMaxSizeBytes = (file) =>
  isDocumentVideoFile(file) ? DOCUMENT_MAX_VIDEO_SIZE_BYTES : DOCUMENT_MAX_FILE_SIZE_BYTES;

export const getDocumentMaxSizeError = (file) =>
  isDocumentVideoFile(file) ? DOCUMENT_MAX_VIDEO_SIZE_ERROR : DOCUMENT_MAX_FILE_SIZE_ERROR;

export const getFileTypeFromName = (fileName = '') => {
  const parts = fileName.split('.');
  if (parts.length < 2) {
    return '-';
  }
  return parts.pop().toUpperCase();
};

/** Visual category used for type chips / placeholders (DOC covers docx, etc.). */
export const DOCUMENT_FILE_TYPE_CATEGORY = {
  DOC: 'DOC',
  XLS: 'XLS',
  PPT: 'PPT',
  PDF: 'PDF',
  IMG: 'IMG',
  VID: 'VID'
};

export const DOCUMENT_FILE_TYPE_CATEGORY_STYLES = {
  [DOCUMENT_FILE_TYPE_CATEGORY.DOC]: { background: '#4697E024', color: '#4697E0' },
  [DOCUMENT_FILE_TYPE_CATEGORY.XLS]: { background: '#1E9E5A24', color: '#1E9E5A' },
  [DOCUMENT_FILE_TYPE_CATEGORY.PPT]: { background: '#C43E1C24', color: '#C43E1C' },
  [DOCUMENT_FILE_TYPE_CATEGORY.PDF]: { background: '#E1444424', color: '#E14444' },
  [DOCUMENT_FILE_TYPE_CATEGORY.IMG]: { background: '#E08A2E24', color: '#E08A2E' },
  [DOCUMENT_FILE_TYPE_CATEGORY.VID]: { background: '#CA48AE24', color: '#667380' }
};

export const getDocumentFileTypeCategory = (fileName = '') => {
  const ext = getFileTypeFromName(fileName);
  if (ext === 'PDF') {
    return DOCUMENT_FILE_TYPE_CATEGORY.PDF;
  }
  if (ext === 'DOC' || ext === 'DOCX') {
    return DOCUMENT_FILE_TYPE_CATEGORY.DOC;
  }
  if (ext === 'XLS' || ext === 'XLSX') {
    return DOCUMENT_FILE_TYPE_CATEGORY.XLS;
  }
  if (ext === 'PPT' || ext === 'PPTX') {
    return DOCUMENT_FILE_TYPE_CATEGORY.PPT;
  }
  if (ext === 'JPG' || ext === 'JPEG' || ext === 'PNG') {
    return DOCUMENT_FILE_TYPE_CATEGORY.IMG;
  }
  if (ext === 'MP4') {
    return DOCUMENT_FILE_TYPE_CATEGORY.VID;
  }
  return null;
};

export const getDocumentFileTypeChipLabel = (fileName = '') => {
  const category = getDocumentFileTypeCategory(fileName);
  if (category === DOCUMENT_FILE_TYPE_CATEGORY.VID) {
    return category;
  }
  const fileType = getFileTypeFromName(fileName);
  return fileType === '-' ? category || '?' : fileType;
};

export const isDocumentInlinePreviewable = (fileName = '') => {
  const category = getDocumentFileTypeCategory(fileName);
  return (
    category === DOCUMENT_FILE_TYPE_CATEGORY.PDF ||
    category === DOCUMENT_FILE_TYPE_CATEGORY.IMG ||
    category === DOCUMENT_FILE_TYPE_CATEGORY.VID
  );
};

export const getDocumentNameFromFile = (file) => {
  if (!file?.name) {
    return '';
  }
  return file.name.replace(/\.[^/.]+$/, '');
};
